import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  SYMBOL_BY_CODE,
  toothType,
  perioSummary,
  arcadeLayout,
  ARCADE_VIEW,
  shapeSamples,
  shapeCusps,
  arcadeOutward,
} from "@/data/dentalCases";

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
const lighten = ([r, g, b]) => [
  Math.round(r + (255 - r) * 0.72),
  Math.round(g + (255 - g) * 0.72),
  Math.round(b + (255 - b) * 0.72),
];

// Draw the occlusal-view anatomical arcade (same layout as the on-screen SVG).
function drawArcadePDF(doc, species, chart, perioChart, x0, y0, scale) {
  const layout = arcadeLayout(species);
  const X = (vx) => x0 + vx * scale;
  const Y = (vy) => y0 + vy * scale;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(40);
  doc.text("Anatomical arcade (occlusal view)", x0, y0 - 6);
  doc.setDrawColor(210, 215, 222);
  doc.setLineWidth(0.4);
  doc.line(X(ARCADE_VIEW.midX), Y(70), X(ARCADE_VIEW.midX), Y(ARCADE_VIEW.h - 70));

  layout.forEach((tt) => {
    const codes = chart[tt.id] || [];
    const fillRGB = codes.length ? hexToRgb(SYMBOL_BY_CODE[codes[0]].color) : null;
    const rad = (tt.rot * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const tx = (p) => {
      const rx = p[0] * cos - p[1] * sin;
      const ry = p[0] * sin + p[1] * cos;
      return [X(tt.x + rx), Y(tt.y + ry)];
    };
    const pts = shapeSamples(tt.shape, tt.W, tt.L).map(tx);
    if (fillRGB) {
      doc.setFillColor(...lighten(fillRGB));
      doc.setDrawColor(...fillRGB);
    } else {
      doc.setFillColor(238, 242, 246);
      doc.setDrawColor(30, 41, 59);
    }
    doc.setLineWidth(0.5);
    const deltas = pts.slice(1).map((p, i) => [p[0] - pts[i][0], p[1] - pts[i][1]]);
    doc.lines(deltas, pts[0][0], pts[0][1], [1, 1], fillRGB ? "FD" : "S", true);
    // cusp marks
    doc.setDrawColor(150, 160, 170);
    doc.setLineWidth(0.3);
    shapeCusps(tt.shape, tt.W, tt.L).forEach((c) => {
      const a = tx([c[0], c[1]]);
      const b = tx([c[2], c[3]]);
      doc.line(a[0], a[1], b[0], b[1]);
    });

    const [ox, oy] = arcadeOutward(tt);
    const nd = tt.L * 0.5 + 16;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(5.4);
    doc.setTextColor(90);
    doc.text(String(tt.id), X(tt.x + ox * nd), Y(tt.y + oy * nd) + 1.6, { align: "center" });
    if (fillRGB) {
      doc.setTextColor(...fillRGB);
      doc.setFontSize(5);
      doc.text(codes.join("·"), X(tt.x), Y(tt.y) + 1.6, { align: "center" });
    }
    const ps = perioSummary(perioChart[tt.id]);
    if (ps) {
      doc.setTextColor(180, 83, 9);
      doc.setFontSize(4.4);
      const pd = tt.L * 0.5 + 10;
      doc.text(ps, X(tt.x - ox * pd), Y(tt.y - oy * pd) + 1.2, { align: "center" });
    }
  });
  return Y(ARCADE_VIEW.h);
}

export function exportDentalPDF(dentalCase, chart, perioChart, result) {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("DENTAL CHART", 40, 40);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("VetSim · Modified Triadan charting", 40, 54);
  doc.text(new Date().toLocaleString(), pageW - 40, 54, { align: "right" });

  doc.setFontSize(10);
  doc.setTextColor(40);
  doc.text(
    `${dentalCase.name} · ${dentalCase.species === "cat" ? "Feline" : "Canine"} · ${dentalCase.breed} · ${dentalCase.age} · ${dentalCase.title}`,
    40,
    74
  );
  doc.setFontSize(8);
  doc.setTextColor(90);
  doc.text(doc.splitTextToSize(dentalCase.briefing, pageW - 80), 40, 88);

  const arcScale = (pageW - 80) / ARCADE_VIEW.w;
  const arcBottom = drawArcadePDF(doc, dentalCase.species, chart, perioChart, 40, 126, arcScale);

  const charted = Object.entries(chart)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([tooth, codes]) => [
      tooth,
      toothType(Number(tooth)),
      codes.map((c) => `${c} (${SYMBOL_BY_CODE[c].name})`).join(", "),
    ]);

  autoTable(doc, {
    startY: arcBottom + 16,
    head: [["Tooth", "Type", "Findings charted"]],
    body: charted.length ? charted : [["—", "—", "No findings charted"]],
    headStyles: { fillColor: [13, 148, 136], fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    margin: { left: 40, right: 40 },
  });

  const chartedPerio = Object.entries(perioChart || {})
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([tooth, p]) => [tooth, toothType(Number(tooth)), perioSummary(p) || "—"]);
  if (chartedPerio.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 14,
      head: [["Tooth", "Type", "Periodontal reading (PD / GI)"]],
      body: chartedPerio,
      headStyles: { fillColor: [180, 83, 9], fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 40, right: 40 },
    });
  }
  if (result) {
    const y = doc.lastAutoTable.finalY + 18;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(40);
    doc.text(`Grade: ${result.grade}  (${result.pct}%)`, 40, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
      `Correct ${result.correct} · Missed ${result.missed} · Incorrect ${result.incorrect.length}`,
      40,
      y + 14
    );
    autoTable(doc, {
      startY: y + 24,
      head: [["Expected finding", "Result"]],
      body: result.findingsResult.map((f) => [
        `${f.tooth} ${toothType(f.tooth)} — ${f.note}`,
        f.satisfied ? "Correct" : f.partial ? `Partial (${f.got.join(", ")})` : "Missed",
      ]),
      headStyles: { fillColor: [30, 41, 59], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 40, right: 40 },
    });
  }

  doc.save(`dental_chart_${dentalCase.name}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
